import React, {
  createContext,
  useReducer,
  useState,
  useContext,
  useEffect,
  useMemo,
} from "react";
import {
  getTrack,
  secondsToHm,
  secondsToHms,
  distanceInPreferredUnit,
  paceInPreferredUnit,
  elevationInPreferredUnit,
  calculateAverageElevationV2,
} from "../../services";

import Map from "./map";
import SidebarTop from "../sidebar-top/sidebar-top";
import ElevationProfileD3 from "./elevation-profiled3";
import WaypointTable from "./waypoint-table";
import ActivityEditable from "./activity-editable";

import "./details.css";
import { reducer } from "./details.reducer";

import {
  getAllWaypoints,
  deleteWaypoint,
  addWaypoint,
  moveWaypoint,
} from "../../services";

import CustomModal from "../custommodal/CustomModal";

// Instead of prop drilling our state and dispatch it's easier to create context
const DetailsContext = createContext(null);

export function useDetailsContext() {
  return useContext(DetailsContext);
}

// Initialize state
const initialState = {
  isIndicatorVisible: false,
  waypoints: [],
};


const Details = ({
  activityId,
  selectedActivity,
  isUserLoggedIn,
  stripeLevel,
  allowCookies,
  userPrefUnitLabels,
}) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [modalPayload, setModalPayload] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [activityDetails, setActivityDetails] = useState();
  // console.log("Details: activityId is " + activityId);
  
  const closeModalHandler = () => {
    setShowModal(false);
  };

  const handleTimeFormatError = () => {
    setModalPayload({
      btnOkCb: () => {
        setShowModal(false);
      },
      bodyText: "The time should be in HH:MM:SS format",
    });
    setShowModal(true);
  };

  const handleDeleteWaypoint = (wptId, activityId) => {
    setModalPayload({
      btnOkCb: () => {
        deleteWaypoint(wptId)
          .then(() => getAllWaypoints(activityId))
          .then((res) => dispatch({ type: "FETCH_WAYPOINTS", payload: res }));
        setShowModal(false);
      },
      bodyText: "Are you sure you want to remove this waypoint?",
      title: "Delete Waypoint",
      okButtonText:"Delete",
    });
    setShowModal(true);
  };
  
  const handleAddWaypoint = (activityId, index) => {
    setModalPayload({
      btnOkCb: () => {
        addWaypoint(activityId, index)
        .then(() => getAllWaypoints(activityId))
        .then((res) => dispatch({ type: "FETCH_WAYPOINTS", payload: res }));
          setShowModal(false);
      },
      bodyText: "Would you like to add a new Waypoint?",
      title: "Add Waypoint",
      okButtonText:"Add",
    });
    setShowModal(true);
  };

  const handleMoveWaypoint = (pointId, index, pointTitle) => {
    moveWaypoint(pointId, index, pointTitle)
    .then(() => getAllWaypoints(activityId))
    .then((res) => dispatch({ type: "FETCH_WAYPOINTS", payload: res }));
  };

  const handleActiveWaypoint = (wptId) => {
    dispatch({ type: "SET_ACTIVE_WAYPOINT", payload: wptId });
  };

  // we need this in this context because outletcontext doesn't go further
  useEffect(() => {
    dispatch({ type: "SET_USER_LOGGED_IN", payload: isUserLoggedIn });
  }, [isUserLoggedIn]);
  
  useEffect(() => {
    dispatch({ type: "SET_COOKIES_ALLOWANCE", payload: allowCookies });
  }, [allowCookies]);
  
  useEffect(() => {
    dispatch({ type: "SET_PREF_UNITS", payload: userPrefUnitLabels });
  }, [userPrefUnitLabels]);
  
  useEffect(() => {
    dispatch({ type: "SET_STRIPE_LEVEL", payload: stripeLevel });
  }, [stripeLevel]);

  // load the waypoints ...
  useMemo(() => {
    // console.log("useMemo: FETCH_WAYPOINTS id: " + activityId);
    if (activityId) {
      getAllWaypoints(activityId)
      .then((res) => {
        dispatch({ type: "FETCH_WAYPOINTS", payload: res });
        // console.log(`useMemo: FETCH_WAYPOINTS got waypoints for id: ${activityId} - ${res.length}`);
      })
    }
  }, [activityId]);

  useMemo(() => {
    if (activityId) {
      getTrack(activityId).then((res) => {
        dispatch({ type: "SET_ROUTE", payload: res });
      });
    }
  }, [activityId]);

  useMemo(() => {
    if (state.selectedRoute?.route.length > 0 && state.waypoints?.length > 0) {
      // console.log("Details: update activityDetails for activity");
      const lastWaypoint = state.waypoints[state.waypoints.length - 1];
      const timeSplit = lastWaypoint.elapsedTime.split(":").reduce((acc, val) => (60 * acc) + +val, 0);
      const totalDistance = distanceInPreferredUnit(lastWaypoint.distance / 1000);
      const paceTime = secondsToHms(paceInPreferredUnit(timeSplit / totalDistance));

      const updatedDetails = {
        ...selectedActivity,
        totalPositiveElevation: elevationInPreferredUnit(state.selectedRoute.totalPositiveElevation),
        totalNegativeElevation: elevationInPreferredUnit(state.selectedRoute.totalNegativeElevation),
        averageElevation: elevationInPreferredUnit(calculateAverageElevationV2(state.selectedRoute)),
        totalPositiveElevationRounded: Math.round(elevationInPreferredUnit(state.selectedRoute.totalPositiveElevation)),
        totalNegativeElevationRounded: Math.round(elevationInPreferredUnit(state.selectedRoute.totalNegativeElevation)),
        averageElevationRounded: Math.round(elevationInPreferredUnit(calculateAverageElevationV2(state.selectedRoute))),
        activityTime: secondsToHm(timeSplit),
        totalDistance: totalDistance,
        totalDistanceRounded: Number(totalDistance.toFixed(2)),
        paceTime: paceTime,
      };
      setActivityDetails(updatedDetails);
    }
  }, [selectedActivity, state.selectedRoute, state.userPrefUnits, state.waypoints]);
    
  return (
    <DetailsContext.Provider value={[state, dispatch]}>
      <div className="main-container">
        {showModal &&  (<CustomModal
              payload={modalPayload}
              showModal={showModal}
              closeModalHandler={closeModalHandler}
            />)}
        <div className="main-map-top">
          <div className="row">
              {!activityId && (
                <div style={{ textAlign: "center" }}>
                  Please select or create an activity
                </div>
              )}
              {activityDetails && (
                <ActivityEditable
                  id={activityId}
                  activityDetails={activityDetails}
                />
              )}
          </div>
          <div className="row sidebar-top-line">
            <SidebarTop
              id={activityId}
              activityDetails={activityDetails}
              userPrefUnits={state.userPrefUnits}
            />
          </div>
        </div>
        <div className="main-map-bottom">
        {activityId && (
            <WaypointTable 
              waypoints={state.waypoints}
              route={state.selectedRoute?.route}
              userPrefUnits={state.userPrefUnits}
              handleActiveWaypoint={handleActiveWaypoint}
            />
        )}
          {activityId && (  
            <div className="main-map-container">
              {state.selectedRoute && state.waypoints && (
              <Map
                activityId={activityId}
                timeFormatError={handleTimeFormatError}
                handleAddWaypoint={handleAddWaypoint}
                handleDeleteWaypoint={handleDeleteWaypoint}
              />)}
              {state.selectedRoute && state.waypoints && (
                  <ElevationProfileD3 
                    activityId={activityId}
                    handleAddWaypoint={handleAddWaypoint}
                    handleMoveWaypoint={handleMoveWaypoint}
                    />
              )}
            </div>
          )}
        </div>
      </div>
    </DetailsContext.Provider>
  );
};

export default Details;
